import React, { useState, useMemo, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  Select as AriaSelect,
  Label,
  Button,
  Popover,
  ListBox,
  ListBoxItem,
  SelectValue,
  Text,
  FieldError,
} from 'react-aria-components';
import clsx from 'clsx';
import { colors } from '@shared/theme';
import { CustomCheckbox } from '@shared/components/CustomCheckbox/CustomCheckbox';
import { CustomTextInput } from '@shared/components/CustomTextInput/CustomTextInput';
import { CustomButton } from '@shared/components/CustomButton/CustomButton';
import styles from './CustomSelectAlt.module.css';

export interface SelectOptionAlt {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface CustomSelectAltProps {
  /**
   * Select label
   */
  label?: string;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text to display below the select
   */
  helperText?: string;
  /**
   * Full width select
   */
  fullWidth?: boolean;
  /**
   * Options to display
   */
  options: SelectOptionAlt[];
  /**
   * Selected value(s)
   */
  value?: string | string[];
  /**
   * Change handler
   */
  onChange?: (value: string | string[]) => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Enable multi-select
   */
  multiSelect?: boolean;
  /**
   * Enable search functionality
   */
  searchable?: boolean;
  /**
   * Placeholder for search input
   */
  searchPlaceholder?: string;
  /**
   * Message to display when no options are found
   */
  noOptionsMessage?: string;
  /**
   * Enable option removal
   */
  removable?: boolean;
  /**
   * Callback when an option is removed
   */
  onRemove?: (value: string) => void;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Enable create new option functionality
   */
  creatable?: boolean;
  /**
   * Callback when creating a new option
   */
  onCreate?: (value: string, autoSelect?: boolean) => void;
  /**
   * Callback when options array changes
   */
  onOptionsChange?: (options: SelectOptionAlt[]) => void;
  /**
   * Label for the create input
   */
  createLabel?: string;
  /**
   * Placeholder for the create input
   */
  createPlaceholder?: string;
  /**
   * Button text for create action
   */
  createButtonText?: string;
  /**
   * Auto-select created value
   */
  autoSelectCreated?: boolean;
  /**
   * Maximum character length for create input
   */
  createMaxLength?: number;
  /**
   * Size of the select
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Custom border color (only applies to default state, not open/error)
   */
  borderColor?: string;
}

/**
 * CustomSelectAlt component - an enhanced select with search, multi-select, and removal capabilities
 */
export const CustomSelectAlt: React.FC<CustomSelectAltProps> = ({
  label,
  error,
  helperText,
  fullWidth = false,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  multiSelect = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  noOptionsMessage = 'No options found',
  removable = false,
  onRemove,
  disabled = false,
  creatable = false,
  onCreate,
  onOptionsChange,
  createLabel = 'Create new option',
  createPlaceholder = 'Enter option name',
  createButtonText = 'Create',
  autoSelectCreated = false,
  createMaxLength,
  size = 'medium',
  borderColor,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [createValue, setCreateValue] = useState('');
  const [createError, setCreateError] = useState('');
  const [popoverPlacement, setPopoverPlacement] = useState<'top' | 'bottom'>('bottom');
  const placementRef = useRef<'top' | 'bottom'>('bottom');
  const updateScheduledRef = useRef(false);
  const [localNewOptions, setLocalNewOptions] = useState<SelectOptionAlt[]>([]);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);

  const hasError = !!error;

  // Merge external options with locally created ones
  const mergedOptions = useMemo(() => {
    const optionValues = new Set(options.map(opt => opt.value));
    const uniqueLocalOptions = localNewOptions.filter(opt => !optionValues.has(opt.value));
    return [...options, ...uniqueLocalOptions];
  }, [options, localNewOptions]);

  // Normalize value to array for easier handling
  const selectedValues = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Clear tempSelectedValues once the values are confirmed in the parent's value prop
  useEffect(() => {
    if (tempSelectedValues.length > 0) {
      const confirmedValues = tempSelectedValues.filter(temp => selectedValues.includes(temp));
      if (confirmedValues.length > 0) {
        // Remove confirmed values from temp
        setTempSelectedValues(prev => prev.filter(temp => !selectedValues.includes(temp)));
      }
    }
  }, [selectedValues, tempSelectedValues]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return mergedOptions;
    return mergedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [mergedOptions, searchQuery]);

  const handleRemove = (valueToRemove: string, e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    const option = mergedOptions.find(opt => opt.value === valueToRemove);
    const confirmed = window.confirm(
      `Are you sure you want to remove "${option?.label || valueToRemove}" from the options list?`
    );
    
    if (!confirmed) return;
    
    // Remove from options list
    const updatedOptions = options.filter(opt => opt.value !== valueToRemove);
    const updatedLocalOptions = localNewOptions.filter(opt => opt.value !== valueToRemove);
    onOptionsChange?.(updatedOptions);
    setLocalNewOptions(updatedLocalOptions);
    
    // Remove from selected values if selected
    if (multiSelect && selectedValues.includes(valueToRemove)) {
      onChange?.(selectedValues.filter(v => v !== valueToRemove));
    } else if (!multiSelect && value === valueToRemove) {
      onChange?.('');
    }
    
    onRemove?.(valueToRemove);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Collapse tags when dropdown opens
      setTagsExpanded(false);
    }
    if (!open) {
      setSearchQuery('');
      setCreateValue('');
      setCreateError('');
    }
  };

  const handleCreate = () => {
    const trimmedValue = createValue.trim();
    if (!trimmedValue) return;
    
    const normalizedValue = trimmedValue.toLowerCase();
    const optionExists = mergedOptions.some(
      option => option.label.trim().toLowerCase() === normalizedValue
    );
    
    if (optionExists) {
      setCreateError('This option already exists');
      return;
    }
    
    // Create and add new option
    const newOption: SelectOptionAlt = {
      value: trimmedValue,
      label: trimmedValue
    };
    
    const updatedOptions = [...options, newOption];
    
    // Clear form
    setCreateValue('');
    setCreateError('');
    setSearchQuery('');
    
    // Use flushSync to ensure all state updates happen synchronously
    flushSync(() => {
      // Set temp value immediately for instant visual feedback (if auto-select enabled)
      if (autoSelectCreated) {
        setTempSelectedValues(prev => [...prev, trimmedValue]);
      }
      
      // If no onCreate, add to local options for immediate display
      if (!onCreate) {
        setLocalNewOptions(prev => [...prev, newOption]);
      }
    });
    
    // Always notify parent to add the option to options array
    onOptionsChange?.(updatedOptions);
    
    // If auto-select is enabled, update the value
    if (autoSelectCreated) {
      // Update the value
      if (multiSelect) {
        const currentValues = Array.isArray(value) ? value : [];
        const newValues = [...currentValues, trimmedValue];
        onChange?.(newValues);
      } else {
        onChange?.(trimmedValue);
        setIsOpen(false);
      }
    }
    
    // Call onCreate callback for notification (if provided)
    onCreate?.(trimmedValue, autoSelectCreated);
  };

  const handleCreateInputChange = (value: string) => {
    setCreateValue(value);
    if (createError) setCreateError('');
  };

  // Check if option can be created
  const canCreateOption = useMemo(() => {
    const trimmedValue = createValue.trim();
    if (!trimmedValue) return false;
    
    const normalizedValue = trimmedValue.toLowerCase();
    return !mergedOptions.some(
      option => option.label.trim().toLowerCase() === normalizedValue
    );
  }, [createValue, mergedOptions]);

  // Sync placement from ref to state whenever needed
  useEffect(() => {
    if (!isOpen) {
      // Reset to bottom when closed
      if (popoverPlacement !== 'bottom') {
        setPopoverPlacement('bottom');
      }
    }
  }, [isOpen, popoverPlacement]);

  // Clean up local options when they appear in external options
  useEffect(() => {
    if (localNewOptions.length > 0) {
      const optionValues = new Set(options.map(opt => opt.value));
      const stillPending = localNewOptions.filter(opt => !optionValues.has(opt.value));
      if (stillPending.length !== localNewOptions.length) {
        setLocalNewOptions(stillPending);
      }
    }
  }, [options, localNewOptions]);

  // Clear temp selected values when parent confirms them in the value prop
  useEffect(() => {
    if (tempSelectedValues.length > 0) {
      const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
      const stillTemp = tempSelectedValues.filter(temp => !currentValues.includes(temp));
      if (stillTemp.length !== tempSelectedValues.length) {
        setTempSelectedValues(stillTemp);
      }
    }
  }, [value, tempSelectedValues]);

  // Size configurations
  const sizeConfig = {
    small: {
      fontSize: '14px',
    },
    medium: {
      fontSize: '16px',
    },
    large: {
      fontSize: '18px',
    },
  };

  const currentSize = sizeConfig[size];

  return (
    <AriaSelect
      className={clsx(
        styles.wrapper,
        { [styles.wrapperFullWidth]: fullWidth },
        className
      )}
      style={{
        ...(borderColor ? { '--custom-border-color': borderColor } as React.CSSProperties : {}),
      }}
      isDisabled={disabled}
      selectionMode={multiSelect ? 'multiple' : 'single'}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      {...(error ? { validationState: 'invalid' as const } : {})}
      {...(multiSelect 
        ? { 
            selectedKeys: new Set([
              ...(Array.isArray(value) ? value : []),
              ...tempSelectedValues
            ]),
            onSelectionChange: (keys: any) => {
              const selectedArray = Array.from(keys as Set<string>);
              
              // Clear temp values when user makes a manual selection
              if (tempSelectedValues.length > 0) {
                setTempSelectedValues([]);
              }
              
              // Call onChange with the selected values
              onChange?.(selectedArray);
            }
          } 
        : { 
            selectedKey: tempSelectedValues[0] || (value as string) || null,
            onSelectionChange: (key: any) => {
              setTempSelectedValues([]);
              onChange?.(key === null ? '' : key as string);
            }
          }
      )}
    >
      {label && <Label className={styles.label}>{label}</Label>}
      
      <Button
        className={clsx(
          styles.trigger,
          styles[`trigger${size.charAt(0).toUpperCase() + size.slice(1)}`],
          {
            [styles.triggerOpenBottom]: isOpen && popoverPlacement === 'bottom',
            [styles.triggerOpenTop]: isOpen && popoverPlacement === 'top',
            [styles.triggerError]: hasError,
            [styles.triggerDisabled]: disabled,
            [styles.triggerCustomBorder]: !!borderColor,
          }
        )}
      >
        <SelectValue<SelectOptionAlt>
          className={styles.triggerContent}
        >
          {({ selectedText, isPlaceholder, selectedItems, state }) => {
            if (multiSelect) {
              // Use selectedItems from SelectValue render props
              const items = selectedItems?.filter(item => item != null) || [];
              
              if (items.length > 0) {
                // Maximum tags to show in collapsed state (adjust as needed)
                const maxVisibleTags = 3;
                // Always collapse when dropdown is open, or when not expanded and dropdown is closed
                const shouldCollapse = items.length > maxVisibleTags && (isOpen || !tagsExpanded);
                const visibleItems = shouldCollapse ? items.slice(0, maxVisibleTags) : items;
                const hiddenCount = items.length - maxVisibleTags;
                
                return (
                  <>
                    {visibleItems.map((item) => (
                      <span key={item.value} className={styles.selectedTag}>
                        <span className={styles.selectedTagText}>{item.label}</span>
                        <span
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Remove from select state using state.setValue
                            if (Array.isArray(state.value)) {
                              state.setValue(state.value.filter((k: any) => k !== item.value));
                            }
                            // Also call onChange for external state management
                            onChange?.(selectedValues.filter(v => v !== item.value));
                          }}
                          role="button"
                          tabIndex={-1}
                          className={styles.removeButton}
                          aria-label={`Remove ${item.label}`}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                    {shouldCollapse && hiddenCount > 0 && (
                      <span 
                        className={clsx(styles.selectedTag, styles.moreTag)}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setTagsExpanded(true);
                        }}
                        role="button"
                        tabIndex={-1}
                        aria-label={`Show ${hiddenCount} more selected items`}
                      >
                        +{hiddenCount} more
                      </span>
                    )}
                  </>
                );
              }
              
              return (
                <span className={styles.placeholder}>{placeholder}</span>
              );
            }
            
            return !isPlaceholder && selectedText ? (
              <span className={styles.singleValue}>
                <span className={styles.selectedTagText}>{selectedText}</span>
              </span>
            ) : (
              <span className={styles.placeholder}>{placeholder}</span>
            );
          }}
        </SelectValue>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className={clsx(styles.chevron, { [styles.chevronOpen]: isOpen })}
        >
          <path d="M5 7.5L10 12.5L15 7.5" stroke={colors.text.disabled} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Button>

      {helperText && !error && (
        <Text slot="description" className={styles.helper}>
          {helperText}
        </Text>
      )}
      
      {error && (
        <FieldError className={clsx(styles.helper, styles.helperError)}>
          {error}
        </FieldError>
      )}

      <Popover
        offset={-1}
        placement="bottom"
      >
        {({ placement }) => {
          // Track placement and schedule state update only if not already scheduled
          const actualPlacement = placement?.startsWith('top') ? 'top' : 'bottom';
          if (actualPlacement !== popoverPlacement && !updateScheduledRef.current) {
            placementRef.current = actualPlacement;
            updateScheduledRef.current = true;
            // Schedule state update after render
            setTimeout(() => {
              setPopoverPlacement(actualPlacement);
              updateScheduledRef.current = false;
            }, 0);
          }

          return (
            <div
              className={clsx(
                styles.popover,
                {
                  [styles.popoverBottom]: actualPlacement === 'bottom',
                  [styles.popoverTop]: actualPlacement === 'top',
                  [styles.popoverCustomBorder]: !!borderColor,
                }
              )}
            >
              {searchable && (
                <input
                  type="search"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className={styles.searchInput}
                  autoFocus
                />
              )}
        
        {filteredOptions.length > 0 ? (
          <ListBox 
            items={filteredOptions} 
            className={clsx(
              styles.listBox,
              {
                [styles.listBoxWithSearch]: searchable && !creatable,
                [styles.listBoxWithCreate]: !searchable && creatable,
                [styles.listBoxWithSearchAndCreate]: searchable && creatable,
              }
            )}
          >
            {(option: SelectOptionAlt) => (
              <ListBoxItem
                id={option.value}
                textValue={option.label}
                isDisabled={option.disabled}
                style={{ outline: 'none', borderTopWidth: '0', borderRightWidth: '0', borderBottomWidth: '0', borderLeftWidth: '0' }}
              >
                {({ isSelected, isHovered }) => {
                  return (
                    <div
                      className={clsx(
                        styles.listItem,
                        {
                          [styles.listItemSelected]: isSelected,
                          [styles.listItemDisabled]: option.disabled,
                          [styles.listItemHovered]: isHovered,
                        }
                      )}
                      style={{ fontSize: currentSize.fontSize }}
                    >
                      <span className={styles.listItemContent}>
                        {multiSelect && (
                          <CustomCheckbox
                            isSelected={isSelected}
                            isDisabled={option.disabled}
                            noInteraction
                          />
                        )}
                        <span>{option.label}</span>
                      </span>
                      {removable && !option.disabled && isHovered && (
                        <span
                          onPointerDown={(e) => handleRemove(option.value, e)}
                          role="button"
                          tabIndex={-1}
                          className={clsx(styles.removeButton, styles.removeButtonLarge)}
                          aria-label={`Remove ${option.label} from options`}
                        >
                          ×
                        </span>
                      )}
                    </div>
                  );
                }}
              </ListBoxItem>
            )}
          </ListBox>
        ) : (
          <ListBox className={styles.listBox}>
            <ListBoxItem 
              id="__empty__" 
              textValue="No options"
              isDisabled
              style={{ outline: 'none', borderTopWidth: '0', borderRightWidth: '0', borderBottomWidth: '0', borderLeftWidth: '0' }}
            >
              <div className={styles.noOptions}>
                {noOptionsMessage}
              </div>
            </ListBoxItem>
          </ListBox>
        )}

        {creatable && (
          <div className={styles.createSection}>
            <div className={styles.createInputWrapper}>
              <CustomTextInput
                label={createLabel}
                placeholder={createPlaceholder}
                value={createValue}
                onChange={handleCreateInputChange}
                maxLength={createMaxLength}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canCreateOption) {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
                error={createError}
                fullWidth
                size={size}
                borderColor={borderColor}
              />
              <CustomButton
                onPress={handleCreate}
                isDisabled={!canCreateOption}
                size={size}
                variant="primary"
              >
                {createButtonText}
              </CustomButton>
            </div>
          </div>
        )}
            </div>
          );
        }}
      </Popover>
    </AriaSelect>
  );
};

CustomSelectAlt.displayName = 'ProdiktCustomSelectAlt';
